<template>
  <JoszakiPage
    :page-name="pageName"
    :tracking-params="trackingParams"
    inner-class="flex flex-col gap-5"
  >
    <template v-if="isLoadingQuestion">
      <CardQuestionDetailsPlaceholder />
      <content-placeholders>
        <content-placeholders-heading />
      </content-placeholders>
      <template v-for="i in 10">
        <CardAnswerPlaceholder :key="i" />
        <content-placeholders
          v-if="i === 3 || i === 8"
          :key="`cta-${i}`"
          rounded
        >
          <content-placeholders-img class="!h-40" />
        </content-placeholders>
      </template>
    </template>
    <template v-else-if="question">
      <BreadCrumbs :links="breadCrumbs" />

      <CardQuestionDetails :question="question" />

      <BlockTender
        text-input
        @requestTender="
          onRequestTender({ source: 'banner-block', description: $event })
        "
      />
      <CardNewAnswer v-if="userCanCreateAnswer" />
      <template v-if="sortedAnswers.length > 0">
        <JoszakiHeading :level="2">
          {{ $t("questionPage.answersOfProfessionals") }}
        </JoszakiHeading>
        <template v-for="(answer, index) in sortedAnswers">
          <CardAnswer :key="answer.id" :answer="answer" />
          <CtaBanner
            v-if="question.profession && index === 1"
            :key="`cta-${index}`"
            title="Profi szakemberre bíznád a munkát?"
            description="Keresd meg a hozzád legközelebb lévő minősített szakembert és kérj árajánlatot 2 perc alatt!"
            button-label="Tovább a jószakira!"
            :external-url="true"
            :button-url="
              localePath({
                name: 'professionPage',
                params: { profession: question.profession.seoName },
              })
            "
          />
          <CtaBanner
            v-if="index === 7"
            :key="`cta-${index}`"
            title="Olvasnál cikkeket hasonló témában?"
            button-label="Irány a blog!"
            :external-url="true"
            button-url="https://joszaki.hu/blog/"
          />
        </template>
        <BlockTender
          v-if="sortedAnswers.length > 3"
          text-input
          @requestTender="
            onRequestTender({ source: 'banner-block', description: $event })
          "
        />
      </template>
      <div v-else class="flex flex-col gap-5 items-center mt-5">
        <IconComponent icon="empty-set" class="!text-4xl text-gray-300" />
        <i18n
          path="questionPage.noAnswersYet.content"
          tag="p"
          class="text-center"
        >
          <template #requestTender>
            <a @click.prevent="onRequestTender({ source: 'no-answers' })">
              {{ $t("questionPage.noAnswersYet.requestTender") }}
            </a>
          </template>
          <template #otherQuestions>
            <NuxtLink :to="localePath({ name: 'questionsPage' })">
              {{ $t("questionPage.noAnswersYet.otherQuestions") }}
            </NuxtLink>
          </template>
        </i18n>
      </div>
      <QuestionPills v-if="question && $mq === 'mobile'" :question="question" />

      <ReportQuestionModal />
      <AttachAnswerModal />
      <EditAnswerModal />
      <DeleteAnswerModal />
      <ReportAnswerModal />
    </template>
  </JoszakiPage>
</template>

<router>
{
name: "questionPage",
}
</router>

<script>
import { useAsync, useRoute, useContext } from "@nuxtjs/composition-api";
import { mapState } from "pinia";
import orderBy from "lodash/orderBy";
import { useUserStore } from "~/stores/user";
import CardNewAnswer from "~/components/_refactored/questionPage/CardNewAnswer.vue";
import CardQuestionDetails from "~/components/_refactored/questionPage/CardQuestionDetails.vue";
import QuestionPills from "~/components/_refactored/questionPage/QuestionPills.vue";
import AttachAnswerModal from "~/components/_refactored/questionPage/AttachAnswerModal.vue";
import ReportQuestionModal from "~/components/_refactored/questionPage/ReportQuestionModal.vue";
import DeleteAnswerModal from "~/components/_refactored/questionPage/DeleteAnswerModal.vue";
import ReportAnswerModal from "~/components/_refactored/questionPage/ReportAnswerModal.vue";
import { useQuestionStore } from "~/stores/question/question";
import EditAnswerModal from "~/components/_refactored/questionPage/EditAnswerModal.vue";
import CtaBanner from "~/components/_refactored/common/CtaBanner.vue";
import CardAnswer from "~/components/_refactored/questionPage/CardAnswer.vue";
import CardQuestionDetailsPlaceholder from "~/components/_refactored/questionPage/CardQuestionDetailsPlaceholder.vue";
import CardAnswerPlaceholder from "~/components/_refactored/questionPage/CardAnswerPlaceholder.vue";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import { useRequestTender } from "~/composables/requestTender";
import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";
import { EVENT_PAGE_NAMES } from "~/data/pageNames";

export default {
  name: "QuestionPage",
  components: {
    BreadCrumbs,
    BlockTender,
    CardAnswerPlaceholder,
    CardQuestionDetailsPlaceholder,
    CardAnswer,
    CtaBanner,
    EditAnswerModal,
    ReportAnswerModal,
    DeleteAnswerModal,
    ReportQuestionModal,
    AttachAnswerModal,
    CardQuestionDetails,
    CardNewAnswer,
    QuestionPills,
  },
  layout: "DefaultLayout",
  setup() {
    const questionStore = useQuestionStore();
    const { error, i18n } = useContext();

    useAsync(async () => {
      const route = useRoute();

      const question = await questionStore.loadQuestion(
        route.value.params.questionSeoName
      );

      if (!question) {
        return error({
          statusCode: 404,
          message: i18n.t("questionPage.questionNotFound"),
        });
      }
    });

    return {
      pageName: EVENT_PAGE_NAMES.QUESTIONS_LANDING_PAGE,
      questionStore,
      ...useRequestTender(),
    };
  },
  data() {
    return {
      trackingParams: null,
    };
  },
  head() {
    return {
      title: this.$t("questionPage.title", {
        question: this.question?.title ?? this.$t("questionPage.defaultTitle"),
      }),
      meta: [
        {
          hid: "description",
          name: "description",
          content: this.question?.body ?? "",
        },
      ],
    };
  },
  computed: {
    ...mapState(useUserStore, {
      me: "me",
    }),
    ...mapState(useQuestionStore, {
      question: "question",
      isLoadingQuestion: "isLoadingQuestion",
    }),
    userCanCreateAnswer() {
      return this.me && this.me.id !== this.question?.person.id;
    },
    sortedAnswers() {
      if (!this.question?.answers) {
        return [];
      }

      // Sort first by upvotes and then creation date
      return orderBy(
        this.question.answers,
        ["upvotes", (answer) => new Date(answer.created)],
        ["desc", "desc"]
      );
    },
    answerCount() {
      return this.question?.answers?.length ?? 0;
    },
    breadCrumbs() {
      const links = [
        {
          name: this.$t("questionsPage.title"),
          page: this.localePath({ name: "questionsPage" }),
        },
      ];

      if (this.question?.profession) {
        links.push({
          name: this.question.profession.name,
          page: this.localePath({
            name: "professionQuestions",
            params: { profession: this.question.profession.seoName },
          }),
        });
      }

      if (this.question?.professionTask?.seoName) {
        links.push({
          name: this.question.professionTask.name,
          page: this.localePath({
            name: "professionTaskQuestions",
            params: { professionTask: this.question.professionTask.seoName },
          }),
        });
      }

      return links;
    },
  },
  beforeMount() {
    this.trackingParams = {
      answerCount: this.answerCount,
      professionSeoName: this.question?.profession?.seoName,
      professionGroupSeoName:
        this.question?.profession?.professionGroup?.seoName,
    };
  },
  methods: {
    onRequestTender({ source, description }) {
      this.requestTender({
        source,
        description,
      });
    },
  },
};
</script>
